import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "../ui/dialog";

import { DatePicker } from "../DatePicker";
import { UserIcon } from "@heroicons/react/24/solid";
import { Plus } from "lucide-react";
import { useCreateProjectMember } from "@/api/ProjetsApi";
import { Member } from "@/types/project";
import { failedToast } from "@/utils/toasts";
import SmallLoader from "../SmallLoader";
import { handleApiErrors } from "@/utils/errorMapper";

interface MemberFormInputs {
  name: string;
  email: string;
  profilePictureUrl?: string;
  lastActivityDate: Date | undefined;
}

const AddProjectMemberModal: React.FC = () => {
  const [newMembers, setNewMembers] = useState<Member[]>([]);
  const [open, setOpen] = useState(false);

  const { createProjectMembers } = useCreateProjectMember();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
    reset,
  } = useForm<MemberFormInputs>({
    defaultValues: { lastActivityDate: undefined },
  });

  const handleAddProjectMember = async () => {
    if (newMembers.length === 0) return;

    setLoading(true);
    const { success, failedMembers, error } =
      await createProjectMembers(newMembers);
    setLoading(false);

    if (success) {
      setNewMembers([]);
      setOpen(false);
    } else {
      console.error("Failed members:", failedMembers);
      if (error) {
        handleApiErrors(error, setError);
      }
      failedToast(`Some members failed to add: ${failedMembers.join(", ")}`);
    }
    setLoading(false);
  };

  const handleSetNewMembers = (data: MemberFormInputs) => {
    const newMember: Member = {
      id: crypto.randomUUID(),
      profilePictureUrl: data.profilePictureUrl || "/images/user.png",
      user_email: data.email,
      addedDate: new Date().toISOString(),
      lastActivityDate: data.lastActivityDate?.toISOString() || "no start date",
      name: data.name,
    };

    setNewMembers((prev) => [...prev, newMember]);

    reset(); // Clear form inputs
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-rajapiNavyBlue rounded-lg text-white py-2 px-6 hover:shadow-md">
          Add Member
        </button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-xl p-6">
        <DialogHeader>
          <div className="w-full flex justify-between items-center">
            <div className="bg-white p-4 rounded-2xl w-fit">
              <UserIcon className="w-6 h-6 text-rajapiBlue" />
            </div>
            {loading && <SmallLoader modal={true} />}
          </div>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(handleSetNewMembers)}
          className="space-y-4 p-4"
        >
          <h2 className="text-xl font-medium text-blue-950">
            Invitez vos membres
          </h2>
          <h2 className="text-xs font-medium text-gray">
            Commencez à créer et gérer votre projet en quelques clics seulement
            !
          </h2>

          {/* Name Input */}
          <div className="border-white border-2 my-2 mt-6 px-3 py-4 bg-inherit rounded-2xl">
            <label className="block text-sm font-medium">Nom</label>
            <input
              type="text"
              className="w-full bg-inherit font-light outline-none text-xs"
              placeholder="Entrez le nom du membre"
              {...register("name", { required: "Le nom est requis" })}
            />
            {errors.name && (
              <p className="text-red-500 text-xs">{errors.name.message}</p>
            )}
          </div>

          {/* Email Input */}
          <div className="border-white border-2 my-2 mt-6 px-3 py-4 bg-inherit rounded-2xl">
            <label className="block text-sm font-medium">
              Adresse électronique
            </label>
            <input
              type="email"
              className="w-full bg-inherit font-light outline-none text-xs"
              placeholder="Entrez l'email du membre"
              {...register("email", {
                required: "L'email est requis",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Format d'email invalide",
                },
              })}
            />
            {errors.email && (
              <p className="text-red-500 text-xs">{errors.email.message}</p>
            )}
          </div>

          {/* Date Picker controlled by React Hook Form */}
          <Controller
            control={control}
            name="lastActivityDate"
            rules={{ required: "La date de début est requise" }}
            render={({ field }) => (
              <DatePicker
                date={field.value}
                label="Date de début"
                setDate={field.onChange}
              />
            )}
          />
          {errors.lastActivityDate && (
            <p className="text-red-500 text-xs">
              {errors.lastActivityDate.message}
            </p>
          )}

          {/* Add Member Button */}
          <button
            type="submit"
            className="mt-2 w-full bg-inherit flex space-x-2 text-blue-950"
            disabled={
              !watch("name") || !watch("email") || !watch("lastActivityDate")
            }
          >
            <Plus className="w-6 h-6" />
            <span>Ajouter un nouveau membre</span>
          </button>
        </form>

        {/* List of Added Members */}
        {newMembers.length > 0 && (
          <ul className="mt-4 space-y-2">
            {newMembers.map((member, index) => (
              <li
                key={index}
                className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-md"
              >
                <div>
                  <p className="text-sm text-gray-500">{member.name}</p>
                  <p className="text-xs text-gray-500">{member.user_email}</p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Modal Buttons */}
        <div className="mt-4 flex justify-center">
          <button
            className="mr-2 bg-rajapiBlue py-4 px-24 rounded-xl text-white text-sm"
            onClick={handleAddProjectMember}
            disabled={newMembers.length < 1}
          >
            Continue
          </button>
          <button
            className="mr-2 bg-white py-4 px-24 rounded-xl text-blue-950 text-sm"
            onClick={() => setOpen(false)}
          >
            Annuler
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddProjectMemberModal;
